import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import MDInput from "ui/MDInput";
import CoverLayout from "layouts/authentication/components/CoverLayout";
import bgImage from "assets/images/bg-sign-up-cover.jpg";
import MDTypography from "ui/MDTypography";
import MDBox from "ui/MDBox";
import { Card } from "@mui/material";
import MDButton from "ui/MDButton";
import Notification from "examples/Notifications";
import AuthService from "services/auth-service";

export function Register() {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
  });

  const [notification, setNotification] = useState(null);
  const [loading, setLoading] = useState(false);

  // -----------------------
  // Handle Input Change
  // -----------------------
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  // -----------------------
  // Validation Logic
  // -----------------------
  const validateForm = () => {
    const { name, email, password } = formData;

    if (!name.trim()) {
      setNotification({ type: "error", message: "Name field cannot be empty." });
      return false;
    }

    if (!email.trim()) {
      setNotification({ type: "error", message: "Email field cannot be empty." });
      return false;
    }

    const validEmailDomains = ["gmail.com", "hotmail.com", "company.com"];
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!emailRegex.test(email)) {
      setNotification({ type: "error", message: "Please enter a valid email address." });
      return false;
    }

    const domain = email.split("@")[1]?.toLowerCase();
    if (!validEmailDomains.includes(domain)) {
      setNotification({
        type: "error",
        message: "Please enter a valid email address.",
      });
      return false;
    }

    if (!password.trim()) {
      setNotification({ type: "error", message: "Password field cannot be empty." });
      return false;
    }

    if (password.trim().length < 8) {
      setNotification({
        type: "error",
        message: "Password must be at least 8 characters long.",
      });
      return false;
    }

    return true;
  };

  // -----------------------
  // Submit Handler
  // -----------------------
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      setLoading(true);
      const response = await AuthService.register(formData);

      // ✅ Success
      setNotification({
        type: "success",
        message: "Registration successful! Redirecting to login...",
      });

      setTimeout(() => navigate("/auth/login"), 2000);
    } catch (error) {
      console.error("Registration error:", error);
      setNotification({
        type: "error",
        message:
          error?.response?.data?.message ||
          error?.response?.data?.error || // 👈 ye line add karo
          error?.message ||

          "Registration failed. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  // -----------------------
  // UI
  // -----------------------
  return (
    <CoverLayout image={bgImage}>
      <Card>
        <MDBox
          variant="gradient"
          bgColor="info"
          borderRadius="lg"
          coloredShadow="info"
          mx={2}
          mt={-3}
          p={2}
          mb={1}
          textAlign="center"
        >
          <MDTypography variant="h4" fontWeight="medium" color="white" mt={1}>
            Sign up
          </MDTypography>
        </MDBox>

        <MDBox pt={4} pb={3} px={3}>
          <MDBox component="form" role="form" onSubmit={handleSubmit}>
            <MDBox mb={2}>
              <MDInput
                type="text"
                label="Name"
                fullWidth
                name="name"
                value={formData.name}
                onChange={handleChange}
              />
            </MDBox>

            <MDBox mb={2}>
              <MDInput
                type="email"
                label="Email"
                fullWidth
                name="email"
                value={formData.email}
                onChange={handleChange}
              />
            </MDBox>

            <MDBox mb={2}>
              <MDInput
                type="password"
                label="Password"
                fullWidth
                name="password"
                value={formData.password}
                onChange={handleChange}
              />
            </MDBox>

            <MDBox mt={4} mb={1}>
              <MDButton
                variant="gradient"
                color="info"
                fullWidth
                type="submit"
                disabled={loading}
              >
                {loading ? "Registering..." : "Register Now"}
              </MDButton>
            </MDBox>

            <MDBox mb={1} textAlign="center">
              <MDTypography variant="button" color="text">
                Already have an account?{" "}
                <MDTypography
                  component={Link}
                  to="/auth/login"
                  variant="button"
                  color="info"
                  fontWeight="medium"
                  textGradient
                >
                  Sign in
                </MDTypography>
              </MDTypography>
            </MDBox>
          </MDBox>
        </MDBox>
      </Card>

      <Notification notification={notification} setNotification={setNotification} />
    </CoverLayout>
  );
}

export default Register;
